import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Upload, Pencil, Trash2, Check, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ImagePicker = ({ value, onChange, label = '圖片' }) => {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const displayUrl = resolveImageUrl(value);

  useEffect(() => { if (open) fetchImages(); }, [open]);

  const fetchImages = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/images`); setImages(r.data); }
    catch { /* silent */ } finally { setLoading(false); }
  };

  const uploadFiles = useCallback(async (files) => {
    const imgs = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return toast.error('請選擇圖片檔案');
    setUploading(true);
    try {
      let lastUrl = '';
      for (const file of imgs) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        lastUrl = res.data.url;
      }
      toast.success(`上傳了 ${imgs.length} 張`);
      fetchImages();
      if (imgs.length === 1 && lastUrl) { onChange(lastUrl); setOpen(false); }
    } catch { toast.error('上傳失敗'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  }, [onChange]);

  return (
    <div className="space-y-1.5">
      {label && <Label>{label}</Label>}

      {/* Main trigger — image thumbnail or empty state */}
      <div className="relative group w-full">
        {displayUrl ? (
          /* Has image — show thumbnail, hover to show actions */
          <div
            className="relative rounded-xl overflow-hidden border-2 border-gray-200 cursor-pointer hover:border-primary transition-colors bg-gray-50"
            onClick={() => setOpen(true)}
          >
            <img
              src={displayUrl}
              alt={label}
              className="w-full object-contain max-h-48"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                className="flex items-center gap-1.5 bg-white text-gray-800 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" /> 更換圖片
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(''); }}
                className="flex items-center gap-1.5 bg-red-500 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-red-600 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" /> 移除
              </button>
            </div>
          </div>
        ) : (
          /* No image — show empty state */
          <div
            onClick={() => setOpen(true)}
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
          >
            <ImageIcon className="mx-auto h-10 w-10 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500 font-medium">點擊選擇或上傳圖片</p>
            <p className="text-xs text-gray-400 mt-1">JPG · PNG · GIF · WebP</p>
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>選擇{label}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-y-auto">
            {/* Drop zone / upload */}
            <div
              onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary hover:bg-gray-50'}`}
            >
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={(e) => uploadFiles(e.target.files)} className="hidden" />
              <Upload className={`mx-auto h-7 w-7 mb-2 ${dragOver ? 'text-primary' : 'text-gray-400'}`} />
              <p className="text-sm text-gray-600">
                {uploading ? '上傳中...' : dragOver ? '放開以上傳' : '拖放圖片上傳，或點擊選擇檔案'}
              </p>
            </div>

            {/* Image grid */}
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">載入中...</div>
            ) : images.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">未有圖片，請上傳</div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {images.map((img) => {
                  const imgUrl = resolveImageUrl(img.url);
                  const isSelected = value === img.url || displayUrl === imgUrl;
                  return (
                    <div
                      key={img.id}
                      onClick={() => { onChange(img.url); setOpen(false); }}
                      className={`group relative rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-gray-300'}`}
                    >
                      <div className="aspect-square bg-gray-100">
                        <img src={imgUrl} alt={img.original_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                      </div>
                      {/* Selected check */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-primary rounded-full p-0.5">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                      {/* Name on hover */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1.5 opacity-0 group-hover:opacity-100 transition-opacity truncate">
                        {img.original_name}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImagePicker;
